import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, MapPinned } from "lucide-react";
import { Container, Img, Reveal, EASE } from "@/components/common/Primitives";
import { useLang } from "@/lib/i18n";
import { SIZES } from "@/lib/images";

/**
 * The hand-drawn village map with living hotspots. Positions are percentages of the illustration (3:2).
 * Hover / focus / tap a pin to read what it is and how far it is from the hotel.
 */
const SPOTS = [
  { id: "hotel", x: 46.5, y: 41, to: "/rom", nn: ["Skånevik Fjordhotel", "51 rom, restaurant og peisestove. Her bur du.", "Sjå romma"], en: ["Skånevik Fjordhotel", "51 rooms, restaurant and fireplace lounge. This is where you stay.", "See the rooms"] },
  { id: "cafe", x: 59, y: 46.5, to: "/mat", nn: ["Strandkaféen", "Steinomnspizza, kaffi og is på kaien. Kvar dag juni–august.", "Sjå menyen"], en: ["Strandkaféen", "Stone-oven pizza, coffee and ice cream on the quay. Every day June–August.", "See the menu"] },
  { id: "beach", x: 79, y: 70, to: "/opplev#peparen", nn: ["Stranda og Peparen", "Sandstrand, svaberg og stupebrett – fem minutt å gå.", "Sjå badeplassane"], en: ["The beach and Peparen", "Sandy beach, smooth rocks and a diving board – a five-minute walk.", "See where to swim"] },
  { id: "quay", x: 19, y: 78, to: "/opplev#kayak", nn: ["Småbåthamna og kaien", "Kajakkar og robåtar. Snøggbåten frå Bergen legg til 100 meter unna.", "Sjå aktivitetane"], en: ["The marina and the quay", "Kayaks and rowing boats. The express boat from Bergen docks 100 metres away.", "See the activities"] },
  { id: "blues", x: 10, y: 29, to: "/arrangement#det-skjer", nn: ["Skånevik Bluesfestival", "Kvar sommar fyller bluesfestivalen bygda med musikk – hotellet er fullt den helga.", "Sjå det som skjer"], en: ["Skånevik Blues Festival", "Every summer the blues festival fills the village with music – the hotel is full that weekend.", "See what's on"] },
  { id: "fjord", x: 88, y: 88, to: "/opplev#nature", nn: ["Fjorden", "Rein og roleg – for kajakk, fiske og bading rett utanfor hotellet.", "Sjå naturen"], en: ["The fjord", "Clean and calm – for kayaking, fishing and swimming right outside the hotel.", "See the nature"] },
];

export const VillageMap = ({ testid = "home-map" }) => {
  const { lang, tx } = useLang();
  const reduce = useReducedMotion();
  const [active, setActive] = useState("hotel");
  const spot = SPOTS.find((s) => s.id === active) || SPOTS[0];
  const [title, text, cta] = lang === "en" ? spot.en : spot.nn;
  const tipLeft = spot.x > 62;
  const tipAbove = spot.y > 55;

  return (
    <section className="py-20 lg:py-32" data-testid={testid} data-active={active}>
      <Container className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center lg:gap-16">
        <Reveal className="lg:col-span-4 lg:order-2">
          <p className="flex items-center gap-3 text-xs font-medium tracking-wide text-muted-foreground"><span className="tabular text-foreground">03</span><span className="h-px w-8 bg-border" aria-hidden />{tx("Bygda", "The village")}</p>
          <h2 className="mt-4 text-[2rem] leading-[1.02] tracking-[-0.03em] text-foreground sm:text-[2.5rem]">{tx("Alt ligg ved kaien", "Everything is by the quay")}</h2>
          <p className="mt-4 max-w-[44ch] text-base leading-relaxed text-muted-foreground sm:text-lg">{tx("Hotellet, Strandkaféen, småbåthamna og stranda deler same tomt. Trykk på punkta i teikninga.", "The hotel, Strandkaféen, the marina and the beach share the same plot. Tap the points in the drawing.")}</p>
          {/* Spot list – doubles as the mobile control */}
          <ul className="mt-6 flex flex-wrap gap-2 lg:mt-8 lg:flex-col lg:gap-0 lg:divide-y lg:divide-border lg:border-y lg:border-border" data-testid={`${testid}-list`}>
            {SPOTS.map((s) => {
              const [t] = lang === "en" ? s.en : s.nn;
              const on = s.id === active;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(s.id)}
                    onFocus={() => setActive(s.id)}
                    onClick={() => setActive(s.id)}
                    className={`flex w-full items-center justify-between gap-3 rounded-full px-3.5 py-2 text-left text-sm font-medium transition-colors lg:rounded-none lg:px-0 lg:py-3 lg:text-[0.9375rem] ${on ? "bg-foreground text-background lg:bg-transparent lg:text-foreground" : "bg-secondary text-foreground lg:bg-transparent lg:text-muted-foreground lg:hover:text-foreground"}`}
                    aria-pressed={on}
                    data-testid={`${testid}-item-${s.id}`}
                  >
                    <span>{t}</span>
                    <span className={`hidden h-1.5 w-1.5 rounded-full lg:block ${on ? "bg-foreground" : "bg-border"}`} aria-hidden />
                  </button>
                </li>
              );
            })}
          </ul>
        </Reveal>

        <Reveal delay={0.05} className="lg:col-span-8 lg:order-1">
          <figure className="relative overflow-hidden rounded-[24px] border border-border bg-card p-3 shadow-float sm:p-4" data-testid={`${testid}-figure`}>
            <div className="relative">
              <Img src="/images/illustrated-village.jpg" alt={tx("Illustrert kart over Skånevik med hotellet, kaien, stranda og Peparen", "Illustrated map of Skånevik with the hotel, the quay, the beach and Peparen")} className="aspect-[3/2]" radius="rounded-[16px]" sizes={SIZES.half} reveal={false} />
              {/* Pins */}
              {SPOTS.map((s, i) => {
                const on = s.id === active;
                const [t] = lang === "en" ? s.en : s.nn;
                return (
                  <motion.button
                    key={s.id}
                    type="button"
                    initial={reduce ? false : { opacity: 0, scale: 0.4 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.5, delay: 0.15 + i * 0.08, ease: EASE }}
                    onMouseEnter={() => setActive(s.id)}
                    onFocus={() => setActive(s.id)}
                    onClick={() => setActive(s.id)}
                    aria-label={t}
                    aria-pressed={on}
                    className="absolute flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full focus-visible:outline-none"
                    style={{ left: `${s.x}%`, top: `${s.y}%` }}
                    data-testid={`${testid}-pin-${s.id}`}
                  >
                    <span className={`absolute inline-flex h-7 w-7 rounded-full bg-white/60 ${on ? "animate-ping" : ""} motion-reduce:animate-none`} style={{ animationDuration: "1.8s" }} aria-hidden />
                    <span className={`relative flex h-5 w-5 items-center justify-center rounded-full border-2 border-white shadow-float transition-transform duration-200 ${on ? "scale-110 bg-foreground" : "bg-primary"}`} aria-hidden>
                      <span className="h-1.5 w-1.5 rounded-full bg-white" />
                    </span>
                  </motion.button>
                );
              })}
              {/* Tooltip (desktop) */}
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={spot.id}
                  initial={reduce ? false : { opacity: 0, y: tipAbove ? 6 : -6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={reduce ? undefined : { opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.22, ease: EASE }}
                  className="pointer-events-none absolute hidden w-[280px] rounded-2xl bg-card/95 p-4 shadow-island ring-hairline backdrop-blur-xl sm:block"
                  style={{
                    ...(tipLeft ? { right: `calc(${100 - spot.x}% + 28px)` } : { left: `calc(${spot.x}% + 28px)` }),
                    ...(tipAbove ? { bottom: `calc(${100 - spot.y}% + 24px)` } : { top: `calc(${spot.y}% + 24px)` }),
                  }}
                  data-testid={`${testid}-tooltip`}
                >
                  <p className="text-[0.9375rem] font-semibold text-foreground">{title}</p>
                  <p className="mt-1 text-sm leading-snug text-muted-foreground">{text}</p>
                  <p className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-foreground">{cta} <ArrowRight className="h-3.5 w-3.5" /></p>
                </motion.div>
              </AnimatePresence>
            </div>
            {/* Caption / mobile detail */}
            <figcaption className="flex items-start justify-between gap-4 px-2 pb-1 pt-4">
              <div className="min-w-0 sm:hidden" data-testid={`${testid}-mobile-detail`}>
                <p className="text-[0.9375rem] font-semibold text-foreground">{title}</p>
                <p className="mt-0.5 text-sm leading-snug text-muted-foreground">{text}</p>
                <Link to={spot.to} className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-foreground underline decoration-border underline-offset-4" data-testid={`${testid}-mobile-link`}>{cta} <ArrowRight className="h-3.5 w-3.5" /></Link>
              </div>
              <p className="hidden items-start gap-2 text-sm text-muted-foreground sm:flex"><MapPinned className="mt-0.5 h-4 w-4 shrink-0 text-foreground" strokeWidth={1.75} aria-hidden />{tx("Skånevik teikna for oss. Hent papirutgåva i resepsjonen.", "Skånevik drawn for us. Pick up the paper version at reception.")}</p>
              <Link to={spot.to} className="hidden shrink-0 items-center gap-1.5 text-sm font-semibold text-foreground underline decoration-border underline-offset-4 transition-colors hover:decoration-foreground sm:inline-flex" data-testid={`${testid}-link`}>{cta} <ArrowRight className="h-3.5 w-3.5" /></Link>
            </figcaption>
          </figure>
        </Reveal>
      </Container>
    </section>
  );
};
